import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { getOAuthState, clearOAuthState, storeAuthorizationCode, getForceCreateMode, clearForceCreateMode } from "@/lib/oauth";
import { useAuthStore } from "@/hooks/useAuthStore";
import { useToast } from "@/hooks/use-toast";
import { parseNormalizedError } from "@/lib/queryClient";
import { postAuthLinkOptions, postAuthLoginOptions } from "@/services/queryOptions/authQueryOptions";
import { useMutation } from "@tanstack/react-query";
import { setUsername } from "@/utils/analytics";
import useModalStore from "@/stores/useModalStore";
import { captureSentryException, setSentryContext, addSentryBreadcrumb } from "@/config/sentryConfig";

export default function OAuthCallback() {
  const [, setLocation] = useLocation();
  const { login } = useAuthStore();
  const { toast } = useToast();
  const [error, setError] = useState<string | null>(null);
  const [provider, setProvider] = useState<'x' | 'google'>('x');
  const { setAccountExistsModal, setAccountManagementModal } = useModalStore();

  // Guard to prevent double execution
  const hasExecuted = useRef(false);

  const xLinkMutation = useMutation(postAuthLinkOptions('x'));
  const googleLinkMutation = useMutation(postAuthLinkOptions('google'));
  const xLoginMutation = useMutation(postAuthLoginOptions('x', false));
  const googleLoginMutation = useMutation(postAuthLoginOptions('google', false));
  const xLoginForceCreateMutation = useMutation(postAuthLoginOptions('x', true));
  const googleLoginForceCreateMutation = useMutation(postAuthLoginOptions('google', true));

  useEffect(() => {
    const handleCallback = async () => {
      // Prevent double execution
      if (hasExecuted.current) {
        return;
      }
      hasExecuted.current = true;

      // Extract authorization code and state from URL first
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get("code");
      const state = urlParams.get("state");
      const error = urlParams.get("error");
      const errorDescription = urlParams.get("error_description");

      // If no OAuth parameters in URL, this is not a valid callback - redirect to home
      if (!code && !state && !error) {
        setLocation("/");
        return;
      }

      // Retrieve stored OAuth state - done inside useEffect to avoid timing issues
      const { codeVerifier, state: storedState, redirectUri, originPath, linkMode } = getOAuthState();
      const isXOAuth = !!codeVerifier;
      const isLinkMode = linkMode === 'link';
      const forceCreateProvider = getForceCreateMode();

      // Set provider for UI display
      setProvider(isXOAuth ? 'x' : 'google');

      // Add Sentry breadcrumb for OAuth flow tracking
      addSentryBreadcrumb('OAuth callback started', {
        provider: isXOAuth ? 'x' : 'google',
        mode: isLinkMode ? 'link' : 'login',
        hasCode: !!code,
        hasState: !!state,
        hasStoredVerifier: !!codeVerifier,
        hasRedirectUri: !!redirectUri,
        forceCreate: !!forceCreateProvider,
      });

      try {
        // Check for OAuth errors
        if (error) {
          throw new Error(errorDescription || error);
        }

        if (!code || !state) {
          throw new Error("Missing authorization code or state parameter");
        }

        // Validate state parameter (CSRF protection)
        if (state !== storedState) {
          throw new Error("State parameter mismatch - possible CSRF attack");
        }

        if (!redirectUri) {
          throw new Error("Missing OAuth state in session storage");
        }

        // Handle link mode vs login mode
        let data;
        if (isLinkMode) {
          // Link mode: link provider to existing account
          const provider = isXOAuth ? 'x' : 'google';
          const payload = isXOAuth 
            ? { code, code_verifier: codeVerifier, redirect_uri: redirectUri }
            : { code, redirect_uri: redirectUri };
          
          if (provider === 'x') {
            data = await xLinkMutation.mutateAsync(payload as any);
          } else {
            data = await googleLinkMutation.mutateAsync(payload as any);
          }

          // Show success message for linking
          toast({
            title: "Account linked!",
            description: `Your ${isXOAuth ? 'X' : 'Google'} account has been linked successfully`,
          });
        } else {
          // Login mode: authenticate
          const provider = isXOAuth ? 'x' : 'google';
          const payload = isXOAuth
            ? { code, code_verifier: codeVerifier, redirect_uri: redirectUri }
            : { code, redirect_uri: redirectUri };

          // Select appropriate mutation based on forceCreate flag
          if (provider === 'x') {
            const mutation = forceCreateProvider ? xLoginForceCreateMutation : xLoginMutation;
            data = await mutation.mutateAsync(payload);
          } else {
            const mutation = forceCreateProvider ? googleLoginForceCreateMutation : googleLoginMutation;
            data = await mutation.mutateAsync(payload);
          }

          // JWT token is automatically set as httpOnly cookie by the backend
          // We only need to store user data in the auth store
          login(
            {
              id: data.user.id,
              username: data.user.username,
              profileImage: data.user.profile_image_url,
              name: data.user.name,
            }
          );

          // Set username for analytics tracking
          setUsername(data.user.username);

          // Show success message
          const isForceCreate = !!forceCreateProvider;
          toast({
            title: isForceCreate ? "Separate account created!" : "Successfully connected!",
            description: isForceCreate
              ? `Your ${isXOAuth ? 'X' : 'Google'} account has been created as a separate account`
              : `Your ${isXOAuth ? 'X' : 'Google'} account has been connected`,
          });
        }

        // Clear OAuth state and force create flag
        clearOAuthState();
        clearForceCreateMode();

        // Redirect based on mode
        if (isLinkMode) {
          // For link mode, redirect back to account management or origin
          setAccountManagementModal({ show: true });
          setLocation(originPath || "/");
        } else {
          // For login mode, check for saved redirect
          const redirectAfterLogin = sessionStorage.getItem("redirectAfterLogin");
          if (redirectAfterLogin) {
            sessionStorage.removeItem("redirectAfterLogin");
            setLocation(redirectAfterLogin);
          } else {
            // Redirect to origin page or default to /app
            setLocation(originPath || "/");
          }
        }
      } catch (err: any) {
        // Parse the normalized error to extract status and details
        const { status, details } = parseNormalizedError(err);

        // Retrieve OAuth state for error context (may have changed)
        const currentOAuthState = getOAuthState();
        const currentProvider = currentOAuthState.codeVerifier ? 'x' : 'google';

        // Set Sentry context with OAuth debugging information
        setSentryContext('oauth_error', {
          provider: currentProvider,
          mode: isLinkMode ? 'link' : 'login',
          errorStatus: status,
          errorDetails: details, // Full error details from backend
          hasCode: !!code,
          hasState: !!state,
          hasStoredState: !!storedState,
          hasCodeVerifier: !!codeVerifier,
          hasRedirectUri: !!redirectUri,
          redirectUri: redirectUri, // This will be sanitized by Sentry
          forceCreate: !!forceCreateProvider,
          stateMatch: state === storedState,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
        });

        // Capture non-409 errors to Sentry (409 is expected for duplicate accounts)
        if (status !== 409) {
          captureSentryException(
            err instanceof Error ? err : new Error(err.error || 'OAuth callback error'),
            {
              errorType: 'oauth_callback_error',
              provider: currentProvider,
              mode: isLinkMode ? 'link' : 'login',
              status,
              details,
            }
          );
        }

        // Check if this is a 409 account exists error
        if (status === 409 && details) {
          // Parse the error response
          const { existingProviders, matchedEmail, suggestedAction, existingUserId } = details;
          
          // Determine attempted provider based on OAuth flow
          const attemptedProvider = isXOAuth ? 'x' : 'google';
          console.log('err 409', attemptedProvider, existingProviders)
          // Get the existing provider (assuming first one if multiple)
          const existingProvider = existingProviders && existingProviders.length > 0 
            ? existingProviders[0] as 'x' | 'google'
            : null;

          if (existingProvider) {
            console.log('Is Account Exist')

            // Store authorization code for "create separate account" flow
            if (code) {
              storeAuthorizationCode(code);
            }

            // Show the account exists modal with options
            setAccountExistsModal({
              show: true,
              provider: attemptedProvider,
              email: matchedEmail,
              existingProvider,
              existingUserId,
              suggestedAction,
            });

            const redirectAfterLogin = sessionStorage.getItem("redirectAfterLogin");
            if (redirectAfterLogin) {
              sessionStorage.removeItem("redirectAfterLogin");
              setLocation(redirectAfterLogin);
            } else {
              setLocation(originPath || "/");
            }
            // Don't clear OAuth state - user needs to make a choice
            // Code is stored in localStorage for AccountExistsDialog to use
            return;
          }
        }

        // For other errors, handle normally
        const errorMessage = err instanceof Error ? err.message : err.error || "Unknown error occurred";
        setError(errorMessage);

        // Clear OAuth state even on error
        clearOAuthState();

        // Show error toast
        toast({
          title: "Connection failed",
          description: errorMessage,
          variant: "destructive",
        });

        // Redirect to origin page or default to /app after a delay
        setTimeout(() => {
          const redirectAfterLogin = sessionStorage.getItem("redirectAfterLogin");
          if (redirectAfterLogin) {
            sessionStorage.removeItem("redirectAfterLogin");
            setLocation(redirectAfterLogin);
          } else {
            setLocation(originPath || "/");
          }
        }, 3000);
      }
    };

    handleCallback();
  }, []);

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center">
      <div className="text-center">
        {error ? (
          <>
            <h1 className="text-2xl font-bold mb-4">Connection Failed</h1>
            <p className="text-gray-400 mb-4">{error}</p>
            <p className="text-sm text-gray-500">Redirecting to home...</p>
          </>
        ) : (
          <>
            <h1 className="text-2xl font-bold mb-4">Connecting your {provider === 'x' ? 'X' : 'Google'} account...</h1>
            <p className="text-gray-400">Please wait while we complete the setup.</p>
            <div className="mt-6 flex justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
